/** Everything a caller knows about a scan that might carry a certificate.
 *  Batch files, history rows and the scan panel each describe themselves as one
 *  of these instead of re-deriving the rule. */
export interface CertificateSource {
    analysisUuid?: string | null;
    /** The AI result, when that scan finished successfully. */
    aiScore?: number | null;
    /** The plagiarism result, when that scan finished successfully. */
    plagiarismScore?: number | null;
    /** A check is still running — the PDF would miss a result that is about to arrive. */
    pending?: boolean;
}

const hasScore = (score?: number | null) => typeof score === "number" && !isNaN(score);

/** The single rule for "this scan has a certificate".
 *
 *  The backend renders the PDF from a SUCCESSFUL `ai` or `plagiarism` task and answers
 *  `404 not_found:analysis_not_completed` when there is neither, so a grammar-only scan,
 *  a failed one, or one the backend rejected on a plan limit has nothing to hand out.
 *  Every download control in the app gates on this — keep them in sync by calling it
 *  rather than re-checking the fields.
 *
 *  It approximates the backend by the presence of a score, which is as close as the
 *  client can get: the list endpoints expose scores, not task status. On the history
 *  page that leaves a residual gap — its serializer reports a score without checking
 *  status and falls back to the legacy `answer` field, so old rows scanned before
 *  ScanData existed still offer a download that 404s. Closing it needs the backend to
 *  say whether a certificate exists.
 *
 *  Deliberately lives in a dependency-free module: the stores read it too, and importing
 *  a component module from a store would close a cycle through `rootStore`. */
export const hasCertificate = ({analysisUuid, aiScore, plagiarismScore, pending}: CertificateSource) =>
    !!analysisUuid && !pending && (hasScore(aiScore) || hasScore(plagiarismScore));
